import { IconButton, Stack, styled, Typography } from "~/common/mui";
import { Link } from "~/common/components/Link";
import { useIsMinDesktopSize, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { useLockBodyScroll } from "~/common/hooks/useLockBodyScroll";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { occUrl, occFetch } from "~/app/api/occ/occFetch";
import { getOS } from "~/common/utils/browserChecks";
import HistoryIcon from "@mui/icons-material/History";
import CloseIcon from "@mui/icons-material/Close";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

interface RecentSearchesProps {
  searchData: {
    recentSearches?: SearchEntry[];
    popularSearches?: SearchEntry[];
    state: "loading" | "loaded" | "error";
  };
  removeEntry: (term: string) => void;
  hide: () => void;
  className?: string | false;
}

export type SearchEntry = {
  term: string;
  url: string;
};

const MAX_ITEMS = 5;
const MAX_ITEMS_MOBILE = 3;

const RecentSearches = ({ searchData, removeEntry, hide, className }: RecentSearchesProps) => {
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();
  const isTablet = useIsMinTabletSize();
  if (!isDesktop) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useLockBodyScroll();
  }

  const [shownRecentSearches, setShownRecentSearches] = useState(searchData.recentSearches);

  const setSlicedRecentSearches = () =>
    setShownRecentSearches(searchData.recentSearches?.slice(0, !isDesktop && !isTablet ? MAX_ITEMS_MOBILE : MAX_ITEMS));

  useEffect(() => setSlicedRecentSearches(), [isTablet, searchData.state]);

  const removeRecentSearchItem = (term: string) => {
    deleteRecentSearchEntry(term);
    removeEntry(term);
    setShownRecentSearches((prevState) => prevState?.filter((entry) => entry.term !== term));
  };

  const os = getOS();
  const isMobileUA = os === "Android" || os === "iOS";

  const refList = useRef<HTMLAnchorElement[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setFocusedIndex((prevIndex) => (prevIndex + 1) % refList.current.length);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setFocusedIndex((prevIndex) => (prevIndex - 1 + refList.current.length) % refList.current.length);
      } else if (event.key === "Escape") {
        event.preventDefault();
        hide();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [refList]);

  useEffect(() => {
    if (focusedIndex !== -1) {
      refList.current[focusedIndex].focus();
    }
  }, [focusedIndex]);

  return (
    <div className={className || undefined}>
      {searchData.recentSearches && searchData.recentSearches.length > 0 && (
        <>
          <Header variant="titleMedium">{t("recentSearches.recent")}</Header>
          {shownRecentSearches?.map((item, index) => (
            <SearchItemContainer tracking-aux-sch-recent-name={item.term} key={index} $enableHover={!isMobileUA}>
              <SearchItemLink
                ref={(el) => {
                  if (el && !refList.current.includes(el)) {
                    refList.current.push(el);
                  }
                }}
                href={item.url}
              >
                <SearchItem direction="row" alignItems="center">
                  <HistoryIcon />
                  <span>{item.term}</span>
                </SearchItem>
              </SearchItemLink>

              <RemoveButton
                onClick={(e) => {
                  removeRecentSearchItem(item.term);
                  e.currentTarget.blur();
                }}
                $disableHover={isMobileUA}
                color="primary"
              >
                <CloseIcon />
              </RemoveButton>
            </SearchItemContainer>
          ))}
        </>
      )}
      {searchData.popularSearches && searchData.popularSearches.length > 0 && (
        <>
          <Header variant="titleMedium">{t("recentSearches.popular")}</Header>
          {searchData.popularSearches?.slice(0, MAX_ITEMS).map((item, index) => (
            <SearchItemContainer tracking-aux-sch-popular-name={item.term} key={index} $enableHover={!isMobileUA}>
              <SearchItemLink
                ref={(el) => {
                  if (el && !refList.current.includes(el)) {
                    refList.current.push(el);
                  }
                }}
                href={item.url}
              >
                <SearchItem direction="row" alignItems="center">
                  <TrendingUpIcon />
                  <span>{item.term}</span>
                </SearchItem>
              </SearchItemLink>
            </SearchItemContainer>
          ))}
        </>
      )}
    </div>
  );
};

export default RecentSearches;

const Header = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const SearchItem = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(1),
  fontWeight: "bold",
}));

const SearchItemContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "$enableHover",
})<{ $enableHover: boolean }>(({ theme, $enableHover }) => ({
  display: "flex",
  padding: theme.spacing(0.5, 2),
  justifyContent: "space-between",
  alignItems: "center",
  ...($enableHover && {
    "&:focus-within": {
      backgroundColor: theme.palette.surfaceContainer.high,
    },
    "@media (hover: hover)": {
      "&:hover": {
        backgroundColor: theme.palette.surfaceContainer.high,
      },
    },
  }),
}));

const SearchItemLink = styled(Link)(({ theme }) => ({
  flex: 1,
  color: `${theme.palette.text.primary} !important`,
}));

const RemoveButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "$disableHover",
})<{ $disableHover: boolean }>(({ theme, $disableHover }) => ({
  padding: "0 !important",
  ...($disableHover && {
    color: `${theme.palette.primary.main} !important`,
  }),
}));

const deleteRecentSearchEntry = async (term: string) => {
  return await occFetch(occUrl(`/search/remove-search-term?term=${encodeURIComponent(term)}`), {
    method: "DELETE",
  });
};
